import { ActionIcon, Badge, Button, Group, Paper, Popover, SegmentedControl, Select, SelectProps, Stack, Text, Tooltip } from "@mantine/core";
import {
    closestCenter,
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { SortingState } from "@tanstack/react-table";
import {
    IconArrowsSort,
    IconCalendar,
    IconGripVertical,
    IconLetterCase,
    IconListCheck,
    IconNumber123,
    IconPlus,
    IconTag,
    IconToggleRight,
    IconTrash,
    IconUser,
    TablerIcon,
} from "@tabler/icons-react";
import { Dispatch, SetStateAction } from "react";
import { useActionPlanToolbarButtonColour } from "@features/action-plans/hooks/useActionPlanToolbarButtonColour";
import { ResetToDefaultButton } from "./ResetToDefaultButton";

type SortColumnValueType = "string" | "number" | "date" | "boolean" | "category" | "person" | "status";

export type SortColumnOption = {
    value: string;
    label: string;
    valueType?: SortColumnValueType;
    sampleValue?: string | number | boolean | Date | null;
};

function getSortOptionValueType(option: Pick<SortColumnOption, "valueType" | "sampleValue">): SortColumnValueType {
    const sampleValue = option.sampleValue;

    if (sampleValue instanceof Date) {
        return "date";
    }

    if (typeof sampleValue === "number") {
        return "number";
    }

    if (typeof sampleValue === "boolean") {
        return "boolean";
    }

    if (typeof sampleValue === "string") {
        return "string";
    }

    return option.valueType ?? "string";
}

function getSortOptionIcon(option: Pick<SortColumnOption, "valueType" | "sampleValue">): TablerIcon {
    switch (getSortOptionValueType(option)) {
        case "number":
            return IconNumber123;
        case "date":
            return IconCalendar;
        case "boolean":
            return IconToggleRight;
        case "category":
            return IconTag;
        case "person":
            return IconUser;
        case "status":
            return IconListCheck;
        case "string":
        default:
            return IconLetterCase;
    }
}

type SortRuleRowProps = {
    rule: SortingState[number];
    option?: SortColumnOption;
    onDirectionChange: (columnId: string, desc: boolean) => void;
    onRemove: (columnId: string) => void;
};

function SortRuleRow({ rule, option, onDirectionChange, onRemove }: SortRuleRowProps) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: rule.id });

    const Icon = getSortOptionIcon(option ?? {});

    return (
        <Group
            ref={setNodeRef}
            gap={6}
            wrap="nowrap"
            p={4}
            style={{
                transform: CSS.Transform.toString(transform),
                transition,
                opacity: isDragging ? 0.7 : 1,
                borderRadius: 6,
                backgroundColor: "light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-7))",
            }}
            {...attributes}
        >
            <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                style={{ cursor: "grab" }}
                {...listeners}
                aria-label="Drag sort rule"
            >
                <IconGripVertical size={15} />
            </ActionIcon>
            <Group c={'dimmed'}>
                <Icon size={14} />
            </Group>
            <Text size="xs" className="grow">
                {option?.label ?? rule.id}
            </Text>
            <Paper withBorder>
                <SegmentedControl
                    size="xs"
                    value={rule.desc ? "desc" : "asc"}
                    onChange={(value) => onDirectionChange(rule.id, value === "desc")}
                    data={[
                        { label: "Asc", value: "asc" },
                        { label: "Desc", value: "desc" },
                    ]}
                />
            </Paper>
            <Tooltip label="Remove column from sort" withArrow>
                <ActionIcon
                    variant="subtle"
                    color="red"
                    size="sm"
                    onClick={() => onRemove(rule.id)}
                    aria-label="Remove sort rule"
                >
                    <IconTrash size={15} />
                </ActionIcon>
            </Tooltip>
        </Group>
    );
}

type SortManagerProps = {
    sorting: SortingState;
    setSorting: Dispatch<SetStateAction<SortingState>>;
    columns: SortColumnOption[];
    onReset?: () => void;
};

export function SortManager({ sorting, setSorting, columns, onReset }: SortManagerProps) {
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor),
    );

    const activeColumnIds = new Set(sorting.map((sort) => sort.id));
    const availableColumns = columns.filter((column) => !activeColumnIds.has(column.value));

    const handleAddSort = (columnId: string) => {
        setSorting((current) => [...current, { id: columnId, desc: false }]);
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) {
            return;
        }

        setSorting((current) => {
            const oldIndex = current.findIndex((rule) => rule.id === active.id);
            const newIndex = current.findIndex((rule) => rule.id === over.id);

            if (oldIndex === -1 || newIndex === -1) {
                return current;
            }

            return arrayMove(current, oldIndex, newIndex);
        });
    };

    const renderSelectOption: SelectProps['renderOption'] = ({ option }) => {
        const sortColumn = columns.find((column) => column.value === option.value);
        const Icon = getSortOptionIcon(sortColumn ?? {});

        return (
            <Group flex="1" gap="xs">
                <Icon size={14} />
                {option.label}
            </Group>
        )
    };

    return (
        <Popover position="bottom-end" shadow="md" withArrow>
            <Popover.Target>
                <Button
                    size="xs"
                    variant={'light'}
                    leftSection={<IconArrowsSort size={14} />}
                    rightSection={sorting.length > 0 ? <Badge size="xs" variant="filled">{sorting.length}</Badge> : undefined}
                >
                    Sort
                </Button>
            </Popover.Target>
            <Popover.Dropdown w={360}>
                <Stack gap="xs">
                    <Group justify="space-between" wrap="nowrap">
                        <Text fw={600} size="xs" c={'dimmed'}>Sort</Text>
                        <ResetToDefaultButton
                            disabled={sorting.length === 0}
                            onClick={() => {
                                if (onReset) {
                                    onReset();
                                    return;
                                }
                                setSorting([]);
                            }}
                        />
                    </Group>
                    <Text size="xs" c={'dimmed'}>Columns are sorted in the order they appear below. Drag to reorder.</Text>
                    <Group gap={6} wrap="nowrap">
                        <Select
                            size="xs"
                            placeholder="Add column"
                            data={availableColumns}
                            onChange={(value) => value ? handleAddSort(value) : null}
                            comboboxProps={{ withinPortal: false }}
                            searchable
                            value={''}
                            className="grow"
                            renderOption={renderSelectOption}
                            leftSection={<IconPlus size={14} />}
                        />
                    </Group>
                    {sorting.length > 0 ? (
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                        >
                            <SortableContext
                                items={sorting.map((rule) => rule.id)}
                                strategy={verticalListSortingStrategy}
                            >
                                <Stack gap={5}>
                                    {sorting.map((rule) => (
                                        <SortRuleRow
                                            key={rule.id}
                                            rule={rule}
                                            option={columns.find((column) => column.value === rule.id)}
                                            onDirectionChange={(columnId, desc) => {
                                                setSorting((current) => current.map((sort) => (
                                                    sort.id === columnId ? { ...sort, desc } : sort
                                                )));
                                            }}
                                            onRemove={(columnId) => {
                                                setSorting((current) => current.filter((sort) => sort.id !== columnId));
                                            }}
                                        />
                                    ))}
                                </Stack>
                            </SortableContext>
                        </DndContext>
                    ) : (
                        <Text size="xs" c="dimmed">
                            No sort columns selected.
                        </Text>
                    )}
                </Stack>
            </Popover.Dropdown>
        </Popover>
    );
}
